// ERP-Sidebar-/Kartenstruktur des Admin-Moduls (16 Bereiche, Pflichtenheft).
// UI-Metadaten (Beschreibungen, geplante Felder) ergänzen die serverseitige
// Wahrheit aus /api/admin/sections.

export type AdminSectionKind = "settings" | "status" | "module" | "planned";

export interface AdminSection {
  id: string;
  label: string;
  icon: string;
  group: "Einstellungen" | "Module & Status" | "Geplant";
  kind: AdminSectionKind;
  description: string;
  settingsCategory?: "general" | "company" | "ai" | "security";
  linkPath?: string;
  plannedFields?: string[];
}

export const ADMIN_SECTIONS: AdminSection[] = [
  // ── Produktive Einstellungen (echt, persistent) ──
  {
    id: "allgemein", label: "Allgemein", icon: "⚙", group: "Einstellungen", kind: "settings",
    settingsCategory: "general",
    description: "Sprache, Zeitzone, Währung, Standardland/-bundesland, MwSt., Standard-Strom- und Einspeisetarif.",
  },
  {
    id: "firmenprofil", label: "Firmenprofil", icon: "🏢", group: "Einstellungen", kind: "settings",
    settingsCategory: "company",
    description: "Unternehmensstammdaten für Angebote, Konzepte, Berichte und PDF-Ausgaben.",
  },
  {
    id: "ki", label: "KI & Expertensystem", icon: "🧠", group: "Einstellungen", kind: "settings",
    settingsCategory: "ai",
    description: "Provider, Modell, Engines. Master-Prompts sind sichtbar, aber nicht editierbar.",
  },
  {
    id: "sicherheit", label: "Sicherheit", icon: "🔒", group: "Einstellungen", kind: "settings",
    settingsCategory: "security",
    description: "Passwortregeln, 2FA, Session-Timeout, Login-Sperren, API-Zugriff.",
  },

  // ── Bestehende Module: nur Statuskarten + Deep Links (keine Doppelpflege) ──
  {
    id: "knowledge", label: "Knowledge Base", icon: "📚", group: "Module & Status", kind: "module",
    linkPath: "/knowledge",
    description: "Single Source of Truth: Komponenten, Tarife, Förderungen, Netzbetreiber.",
  },
  {
    id: "komponenten", label: "Komponenten", icon: "🔧", group: "Module & Status", kind: "module",
    linkPath: "/components",
    description: "Module, Wechselrichter, Speicher, Wallboxen, EMS, Zubehör.",
  },
  {
    id: "foerderungen", label: "Förderungen", icon: "💰", group: "Module & Status", kind: "module",
    linkPath: "/foerderungen",
    description: "Alle 9 österreichischen Bundesländer + Bund.",
  },
  {
    id: "benutzer", label: "Benutzerverwaltung", icon: "👤", group: "Module & Status", kind: "module",
    linkPath: "/users",
    description: "Benutzer, Rollen, Teams, Mandanten.",
  },
  {
    id: "audit", label: "Audit & Logs", icon: "📜", group: "Module & Status", kind: "module",
    linkPath: "/audit",
    description: "Änderungen, Benutzeraktionen, KI-Nutzung, Fehler.",
  },
  {
    id: "systemstatus", label: "Systemstatus", icon: "📊", group: "Module & Status", kind: "status",
    description: "Live-Status: Datenbank, KI-Subsystem, Knowledge Base, Speicher, Laufzeit.",
  },

  // ── Geplant: deaktivierte Karten, keine Fake-UI ──
  {
    id: "hersteller", label: "Hersteller", icon: "🏭", group: "Geplant", kind: "planned",
    description: "Herstellerstammdaten (Fronius, Huawei, SMA, SolarEdge, BYD, …).",
    plannedFields: ["Herstellerdaten", "Supportlinks", "Zertifizierungen", "Dokumente"],
  },
  {
    id: "normen", label: "Normen", icon: "📐", group: "Geplant", kind: "planned",
    description: "OVE, TOR, OIB, EN, IEC, VDE.",
    plannedFields: ["Titel", "Quelle", "Version", "Stand", "Status"],
  },
  {
    id: "netzbetreiber", label: "Netzbetreiber", icon: "🔌", group: "Geplant", kind: "planned",
    description: "TIWAG, KNG, Netz NÖ, Wiener Netze, …",
    plannedFields: ["Einspeisegrenzen", "Dokumente", "Besonderheiten", "Stand"],
  },
  {
    id: "karten", label: "Karten & Geodaten", icon: "🗺", group: "Geplant", kind: "planned",
    description: "OpenStreetMap, Mapbox, Google Maps — API-Keys, Geocoding, Luftbilder.",
    plannedFields: ["Kartenprovider", "API-Keys (Secret)", "Geocoding", "Luftbilder"],
  },
  {
    id: "integrationen", label: "Integrationen", icon: "🔗", group: "Geplant", kind: "planned",
    description: "ServiceHub, Microsoft 365, Outlook/Exchange, SMTP.",
    plannedFields: ["ServiceHub", "Microsoft 365", "SMTP", "API-Tokens (Secret)"],
  },
  {
    id: "rollen", label: "Rollen & Rechte", icon: "🛡", group: "Geplant", kind: "planned",
    description: "Granulare Rechte-Matrix. RBAC-Fundament (admin-only Schreibzugriff) ist bereits aktiv.",
    plannedFields: ["Rollen-Matrix", "Granulare Berechtigungen", "Gast-Zugänge"],
  },
];

export const ADMIN_GROUPS: AdminSection["group"][] = ["Einstellungen", "Module & Status", "Geplant"];

export function findSection(id: string | undefined): AdminSection | undefined {
  return ADMIN_SECTIONS.find((s) => s.id === id);
}
